import React, { useState } from 'react';

import TickIcon from 'icons/ic-tick.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useDestroyAccessControlMutation } from 'components/hooks/accessControlMutations';
import { useCreateUserInviteMutation } from 'components/hooks/userInviteMutations';
import { useDestroyUserMutation } from 'components/hooks/userMutations';
import { useCreateUserPasswordMutation } from 'components/hooks/userPasswordMutations';

import DisableableOptionChoice from 'components/application/DisableableOptionChoice';
import Options from 'components/application/Options';
import SuccessToast from 'components/application/SuccessToast';
import Toast from 'components/application/Toast';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import RemoveModal from 'components/settings/users/RemoveModal';
import ResendInviteModal from 'components/settings/users/ResendInviteModal';
import ResetPasswordModal from 'components/settings/users/ResetPasswordModal';

import { resourceShape } from 'components/helpers/serialisableResources';
import {
  accessTypeHierarchy,
  personDisplayName,
} from 'components/helpers/users';

export default function UserManagementTableRowActions({
  accessControl,
  domainUser,
  isManagingMultiDivisions,
  isSamlSsoEnforced,
  isTrainingRegisterAllowed,
  onEditClick,
  permittedDivisionsCount,
}) {
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();

  const createUserInviteMutation = useCreateUserInviteMutation();
  const createUserPasswordMutation = useCreateUserPasswordMutation();
  const destroyAccessControlMutation = useDestroyAccessControlMutation();
  const destroyUserMutation = useDestroyUserMutation();

  const [isRemoveUserModalOpen, setIsRemoveUserModalOpen] = useState(false);
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);

  const [isResetPasswordModalOpen, setIsPasswordResetModalOpen] =
    useState(false);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.users.UserManagementRow',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const { id } = domainUser;

  const { accessType, email, firstName, invitationStatus, lastName } =
    domainUser.attributes;

  const name = personDisplayName({ attributes: { firstName, lastName } });

  const handleResendSubmit = () => {
    setIsResendModalOpen(false);

    createUserInviteMutation.mutate(
      { userId: id },
      {
        onSuccess: () => {
          breadBoard.addToast(
            <SuccessToast
              message={i18nT('handleResendSubmit.success', {
                interpolation: { escapeValue: false },
                name,
              })}
            />,
          );
        },
        onError: () => {
          breadBoard.addInedibleToast({
            fullMessage: i18nT('handleResendSubmit.error'),
          });
        },
      },
    );
  };

  const handlePasswordResetSubmission = () => {
    setIsPasswordResetModalOpen(false);

    createUserPasswordMutation.mutate(
      { userParams: { email } },
      {
        onSuccess: () => {
          breadBoard.addToast(
            <SuccessToast
              message={i18nT('handlePasswordResetSubmission.success')}
            />,
          );
        },
        onError: () => {
          breadBoard.addInedibleToast({
            fullMessage: i18nT('handlePasswordResetSubmission.error'),
          });
        },
      },
    );
  };

  const handleUserRemoval = () => {
    setIsRemoveUserModalOpen(false);

    destroyUserMutation.mutate(
      { id },
      {
        onSuccess: () => {
          breadBoard.addToast(
            <UserRemovedToast
              handleBurnToast={breadBoard.handleBurnToast}
              name={name}
            />,
          );
        },
        onError: () => {
          breadBoard.addInedibleToast({
            fullMessage: i18nTCommon('settings.errors.removingUser'),
          });
        },
      },
    );
  };

  const handleUserRevoke = () => {
    setIsRemoveUserModalOpen(false);

    destroyAccessControlMutation.mutate(
      { id: accessControl.id },
      {
        onSuccess: () => {
          breadBoard.addToast(
            <UserRemovedToast
              handleBurnToast={breadBoard.handleBurnToast}
              name={name}
              wasRevoked={true}
            />,
          );
        },
        onError: () => {
          breadBoard.addInedibleToast({
            fullMessage: i18nTCommon('settings.errors.removingUser'),
          });
        },
      },
    );
  };

  const isSuperUser = ['administrator', 'primary'].includes(accessType);

  const removalStrategy =
    !isManagingMultiDivisions && permittedDivisionsCount > 1 && !isSuperUser ?
      'revoke'
    : 'delete';

  const isInviteResendable = ['pending', 'expired'].includes(invitationStatus);
  const isPasswordResettable = ['accepted', 'none'].includes(invitationStatus);

  const matchesOrExceedsAccessType =
    accessTypeHierarchy.indexOf(currentActor.user.attributes.accessType) >=
    accessTypeHierarchy.indexOf(accessType);

  let removalBlocker;
  if (!matchesOrExceedsAccessType) {
    removalBlocker = 'lackPermission';
  } else if (accessType === 'primary') {
    removalBlocker = 'isPrimary';
  } else if (id === currentActor.user.id) {
    removalBlocker = 'isSelf';
  }

  const isEditDisabled = !matchesOrExceedsAccessType;

  const isPasswordResetDisabled = accessType === 'regular' && isSamlSsoEnforced;

  const isRemoveDisabled = !!removalBlocker;

  return (
    <>
      <div className='dropdown__container regular-actions'>
        <div className='dropdown'>
          <Options>
            <DisableableOptionChoice
              disabled={isEditDisabled}
              disabledTooltipText={i18nT('options.edit.disabledTooltip')}
              label={i18nTCommon('shared.edit')}
              onClick={(setOptionsOpen) => {
                onEditClick(id);
                setOptionsOpen(false);
              }}
            />
            {isInviteResendable && (
              <DisableableOptionChoice
                disabled={false}
                label={i18nTCommon('shared.resendInvitation')}
                onClick={(setOptionsOpen) => {
                  setIsResendModalOpen(true);
                  setOptionsOpen(false);
                }}
              />
            )}
            {isPasswordResettable && (
              <DisableableOptionChoice
                disabled={isPasswordResetDisabled}
                disabledTooltipText={i18nT(
                  'options.passwordReset.disabledTooltip',
                )}
                label={i18nTCommon('shared.resetPassword')}
                onClick={(setOptionsOpen) => {
                  setIsPasswordResetModalOpen(true);
                  setOptionsOpen(false);
                }}
              />
            )}
            <DisableableOptionChoice
              disabled={isRemoveDisabled}
              disabledTooltipText={
                removalBlocker &&
                i18nT(`options.remove.disabledTooltip.${removalBlocker}`)
              }
              label={i18nTCommon('shared.remove')}
              onClick={(setOptionsOpen) => {
                setIsRemoveUserModalOpen(true);
                setOptionsOpen(false);
              }}
              warning={true}
            />
          </Options>
        </div>
      </div>
      {isResendModalOpen && (
        <ResendInviteModal
          onClose={() => setIsResendModalOpen(false)}
          onResend={handleResendSubmit}
          {...{ firstName, lastName, email }}
        />
      )}
      {isResetPasswordModalOpen && (
        <ResetPasswordModal
          onClose={() => setIsPasswordResetModalOpen(false)}
          onReset={handlePasswordResetSubmission}
          {...{ firstName, lastName, email }}
        />
      )}
      {isRemoveUserModalOpen && (
        <RemoveModal
          {...{ firstName, lastName }}
          divisionName={currentActor.division.attributes.name}
          id={id}
          isTrainingRegisterAllowed={isTrainingRegisterAllowed}
          onClose={() => setIsRemoveUserModalOpen(false)}
          onRemove={handleUserRemoval}
          onRevoke={handleUserRevoke}
          strategy={removalStrategy}
        />
      )}
    </>
  );
}

UserManagementTableRowActions.propTypes = {
  accessControl: resourceShape('accessControl'),
  permittedDivisionsCount: PropTypes.number.isRequired,
  domainUser: resourceShape('user').isRequired,
  isManagingMultiDivisions: PropTypes.bool.isRequired,
  isSamlSsoEnforced: PropTypes.bool.isRequired,
  isTrainingRegisterAllowed: PropTypes.bool.isRequired,
  onEditClick: PropTypes.func.isRequired,
};

function UserRemovedToast({ handleBurnToast, name, wasRevoked = false }) {
  const {
    division: {
      attributes: { name: divisionName },
    },
  } = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.users.UserManagementRow.UserRemovedToast',
  });

  return (
    <Toast
      burn={handleBurnToast}
      burnTimer={5000}
      contents={
        <>
          <div className='toast__icon tw-bg-green-500'>
            <TickIcon
              className='[&_polygon]:tw-fill-white'
              height={20}
              width={20}
            />
          </div>
          <div className='toast__text'>
            {i18nT(wasRevoked ? 'revoked' : 'removed', {
              divisionName,
              interpolation: { escapeValue: false },
              name,
            })}
          </div>
        </>
      }
      modifiers='no-undo'
      toastKey={`userRemovedToast--${name}`}
    />
  );
}
